import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { IMOS_STATUSES, statusClasses, statusTone } from "@/lib/imos";
import { AlertCircle, Factory, Layers, Palette, Ruler, ShieldCheck } from "lucide-react";

export default function ImosProgressPage() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  // Feb 2026 — Project Team column. Resolve staff ids → names via the same
  // two directories the workspace already uses.
  const [imosStaff, setImosStaff] = useState([]);         // IMOS execs + reviewers
  const [interiorDesigners, setInteriorDesigners] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ord, imos, designers] = await Promise.all([
        api.get("/imos/orders"),
        api.get("/imos/executives").catch(() => ({ data: [] })),
        api.get("/design/designers").catch(() => ({ data: [] })),
      ]);
      setOrders(ord.data);
      setImosStaff(imos.data);
      setInteriorDesigners(designers.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  // Combined lookup: `{id → {name, email}}` across both directories, so a
  // single map covers Interior Designer + IMOS Designer + Reviewer.
  const teamById = useMemo(() => {
    const m = {};
    [...imosStaff, ...interiorDesigners].forEach((u) => {
      if (u && u.id) m[u.id] = { name: u.name || u.email || "Unnamed", email: u.email };
    });
    return m;
  }, [imosStaff, interiorDesigners]);

  const resolveTeam = useCallback((order) => ({
    interior: teamById[order.designer_id] || null,
    imos: teamById[order.assigned_executive_id] || null,
    reviewer: teamById[order.reviewer_id] || null,
  }), [teamById]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => {
      const hay = (o.project_name || "") + " " + (o.client_name || "");
      if (hay.toLowerCase().includes(needle)) return true;
      // Feb 2026 — Project Team column is searchable by member name.
      const t = resolveTeam(o);
      const memberBlob = [t.interior?.name, t.imos?.name, t.reviewer?.name]
        .filter(Boolean).join(" ").toLowerCase();
      return memberBlob.includes(needle);
    });
  }, [orders, q, resolveTeam]);

  const blockers = orders.filter((o) => (o.material_checklist || []).some((c) => c.is_blocked || !c.finish || !c.color));

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · IMOS Visibility</p>
        <h1 className="font-display text-3xl text-white mt-1">Production Progress</h1>
        <p className="text-xs text-gray-500 mt-1">Read-only feed of every IMOS order — sync with production drafting and act on material blockers.</p>
      </div>

      <div className="px-10 py-6 space-y-6">
        {blockers.length > 0 && (
          <div className="iz-card p-4 border-l-2 border-l-amber-500" data-testid="crm-blocker-banner">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400">
                {blockers.length} project(s) with open material blockers
              </p>
            </div>
            <p className="text-xs text-gray-400">Push the assigned designers to close pending finish / colour selections.</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project or client…" className="iz-input max-w-sm" data-testid="crm-search"/>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{filtered.length} order(s)</p>
        </div>

        <div className="iz-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
                <th className="text-left px-4 py-3">Project</th>
                <th className="text-left px-4 py-3">Client · City</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Project Team</th>
                <th className="text-left px-4 py-3">Materials</th>
                <th className="text-left px-4 py-3">Last update</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-xs font-mono text-gray-500">No orders match.</td></tr>
              )}
              {filtered.map((o) => {
                const cls = statusClasses(statusTone(o.status));
                const materials = o.material_checklist || [];
                const blocked = materials.filter((c) => c.is_blocked || !c.finish || !c.color).length;
                const team = resolveTeam(o);
                return (
                  <tr key={o.id} className="border-b border-[#0f0f0f] align-top" data-testid={`crm-row-${o.id}`}>
                    <td className="px-4 py-3 text-white">{o.project_name}</td>
                    <td className="px-4 py-3 text-gray-400">{o.client_name} · {o.city}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>
                        {IMOS_STATUSES.find((s) => s.key === o.status)?.label || o.status}
                      </span>
                      {o.status === "sent_to_factory" && <Factory className="inline w-3 h-3 text-emerald-400 ml-2" />}
                    </td>
                    <td className="px-4 py-3" data-testid={`crm-team-${o.id}`}>
                      <TeamStack team={team} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3 h-3 text-[#d4af37]" />
                        <span className="text-xs text-gray-300">{materials.length}</span>
                        {blocked > 0 && (
                          <span className="text-[10px] font-mono text-amber-400 ml-1">· {blocked} blocked</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[10px] font-mono text-gray-500">{new Date(o.updated_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Feb 2026 — Project-Team cell. Stacked chip list of the three stakeholders.
// Muted 'Not Assigned' when the slot is empty so gaps read at a glance.
function TeamStack({ team }) {
  const rows = [
    { role: "Interior", icon: Palette, user: team.interior },
    { role: "IMOS",     icon: Ruler,   user: team.imos },
    { role: "Reviewer", icon: ShieldCheck, user: team.reviewer },
  ];
  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      {rows.map(({ role, icon: Icon, user }) => (
        <div key={role} className="flex items-center gap-2">
          <Icon className={`w-3 h-3 flex-shrink-0 ${user ? "text-[#d4af37]" : "text-gray-700"}`} />
          <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 w-14 shrink-0">{role}</span>
          {user ? (
            <span className="text-xs text-gray-200 truncate" title={user.email || user.name}>{user.name}</span>
          ) : (
            <span className="text-[10px] font-mono italic text-gray-600">Not Assigned</span>
          )}
        </div>
      ))}
    </div>
  );
}
